import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Users, Megaphone, FileSignature, StickyNote, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { platformNotifications, platformUnread, markPlatformRead, markAllPlatformRead } from "@/lib/platformNotifications";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ICONS = { client: Users, campaign: Megaphone, proposal: FileSignature, note: StickyNote, document: FileText };
const TONE = { ok: "text-emerald-500", info: "text-sky-500", warning: "text-amber-500", error: "text-red-500" };

export function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);
  // eslint-disable-next-line no-unused-vars
  const _ = tick;
  const items = platformNotifications(user);
  const unread = platformUnread(user);

  const go = (n) => { markPlatformRead(n.id); refresh(); if (n.link) navigate(n.link); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 text-muted-foreground hover:text-foreground" data-testid="global-notif-bell">
          <Bell className="h-4 w-4" />
          {unread > 0 && <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white" data-testid="global-notif-count">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="global-notif-panel">
        <div className="flex items-center justify-between border-b border-border/60 px-3 py-2.5">
          <span className="text-sm font-semibold text-foreground">Notifiche{unread > 0 ? ` · ${unread}` : ""}</span>
          {unread > 0 && <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => { markAllPlatformRead(user); refresh(); }} data-testid="global-notif-read-all"><CheckCheck className="h-3.5 w-3.5" /> Segna tutte lette</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-3 py-10 text-center text-sm text-muted-foreground">Nessuna notifica.</p>}
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => go(n)} className={`flex w-full items-start gap-3 border-b border-border/40 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 ${n.read ? "opacity-55" : ""}`} data-testid={`global-notif-${n.id}`}>
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted ${TONE[n.tone] || "text-foreground/70"}`}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">{!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />}{n.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{n.body}</div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
